// Admin user-management page (Multi-user Stage B.3).
//
// Lists every user with their cap, MTD spend, disabled flag, and last
// active time. Inline edit for cap. Soft-delete with confirm. Non-admin
// callers get redirected to /chat — the backend would also 404 them.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow, parseISO } from "date-fns";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Navigate } from "react-router-dom";

import { useIsAdmin } from "@/hooks/useIsAdmin";
import {
  type AdminUser,
  type AllowlistEntry,
  createInvite,
  deleteAdminUser,
  fetchAdminUsers,
  fetchInvites,
  patchAdminUser,
  revokeInvite,
} from "@/services/api/admin";
import { cn } from "@/utils/cn";

function relativeTime(iso: string | null): string {
  if (!iso) return "never";
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return "—";
  }
}

function CapCell({
  user,
  onSave,
  disabled,
}: {
  user: AdminUser;
  onSave: (value: string) => void;
  disabled: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user.monthly_cap_usd);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(user.monthly_cap_usd);
          setEditing(true);
        }}
        disabled={disabled}
        className="font-mono text-sm text-espresso-700 hover:text-oxblood-500"
        title="Edit cap"
      >
        ${user.monthly_cap_usd}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span className="font-mono text-sm text-warmgray-500">$</span>
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="w-20 border border-ruleline bg-parchment-50 px-1.5 py-0.5 font-mono text-sm text-espresso-700"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onSave(draft);
            setEditing(false);
          } else if (e.key === "Escape") {
            setEditing(false);
          }
        }}
      />
      <button
        type="button"
        onClick={() => {
          onSave(draft);
          setEditing(false);
        }}
        className="text-xs text-oxblood-500 hover:underline"
      >
        save
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        className="text-xs text-warmgray-400 hover:underline"
      >
        cancel
      </button>
    </div>
  );
}

function UserRow({
  user,
  onPatch,
  onDelete,
  isPending,
}: {
  user: AdminUser;
  onPatch: (userId: string, patch: { monthly_cap_usd?: string; disabled?: boolean }) => void;
  onDelete: (userId: string) => void;
  isPending: boolean;
}) {
  const mtdPct = useMemo(() => {
    const used = parseFloat(user.mtd_spend_usd);
    const cap = parseFloat(user.monthly_cap_usd);
    if (!cap) return 0;
    return Math.min(1, used / cap);
  }, [user.mtd_spend_usd, user.monthly_cap_usd]);

  return (
    <tr
      className={cn(
        "border-b border-ruleline transition-colors hover:bg-parchment-50",
        user.disabled && "opacity-50",
      )}
    >
      <td className="px-3 py-3 align-top">
        <div className="truncate font-mono text-xs text-warmgray-500" title={user.user_id}>
          {user.user_id.replace(/^[^|]+\|/, "")}
        </div>
        <div className="text-[0.65rem] text-warmgray-400">
          {user.welcomed_at ? "welcomed" : "not welcomed"}
        </div>
      </td>
      <td className="px-3 py-3 align-top">
        <CapCell
          user={user}
          disabled={isPending}
          onSave={(v) => onPatch(user.user_id, { monthly_cap_usd: v })}
        />
      </td>
      <td className="px-3 py-3 align-top">
        <div className="font-mono text-sm text-espresso-700">${user.mtd_spend_usd}</div>
        <div className="mt-1 h-1 w-24 bg-parchment-100">
          <div
            className={cn(
              "h-full",
              mtdPct >= 1 ? "bg-oxblood-500" : mtdPct >= 0.8 ? "bg-oxblood-300" : "bg-espresso-400",
            )}
            style={{ width: `${(mtdPct * 100).toFixed(1)}%` }}
          />
        </div>
        <div className="mt-1 text-[0.65rem] text-warmgray-400">
          {user.mtd_trace_count} traces
        </div>
      </td>
      <td className="px-3 py-3 align-top text-xs text-warmgray-500">
        {relativeTime(user.last_active_at)}
      </td>
      <td className="px-3 py-3 align-top">
        {user.disabled ? (
          <span className="inline-block rounded-sm bg-oxblood-50 px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-wider text-oxblood-500">
            disabled
          </span>
        ) : (
          <span className="inline-block text-[0.65rem] uppercase tracking-wider text-warmgray-400">
            active
          </span>
        )}
      </td>
      <td className="px-3 py-3 align-top text-right">
        {user.disabled ? (
          <button
            type="button"
            disabled={isPending}
            onClick={() => onPatch(user.user_id, { disabled: false })}
            className="text-xs text-espresso-700 hover:text-oxblood-500 disabled:opacity-50"
          >
            re-enable
          </button>
        ) : (
          <button
            type="button"
            disabled={isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Soft-delete ${user.user_id}? They will be blocked from /chat but their history stays.`,
                )
              ) {
                onDelete(user.user_id);
              }
            }}
            className="text-xs text-oxblood-500 hover:underline disabled:opacity-50"
          >
            soft-delete
          </button>
        )}
      </td>
    </tr>
  );
}

export function AdminUsers() {
  const queryClient = useQueryClient();
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();

  const usersQuery = useQuery({
    queryKey: ["admin", "users"],
    queryFn: fetchAdminUsers,
    enabled: isAdmin,
  });

  const patchMutation = useMutation({
    mutationFn: ({ userId, patch }: { userId: string; patch: { monthly_cap_usd?: string; disabled?: boolean } }) =>
      patchAdminUser(userId, patch),
    onSuccess: () => {
      toast.success("User updated");
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (err: Error) => {
      toast.error(`Update failed: ${err.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => deleteAdminUser(userId),
    onSuccess: () => {
      toast.success("User soft-deleted");
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (err: Error) => {
      toast.error(`Delete failed: ${err.message}`);
    },
  });

  // Probe is still loading — render a neutral placeholder, not a redirect,
  // so we don't bounce admins who land directly on /admin/users.
  if (isAdminLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl px-6 py-10 text-sm italic text-warmgray-400">
        Checking permissions…
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/chat" replace />;
  }

  const users = usersQuery.data?.users ?? [];
  const anyPending = patchMutation.isPending || deleteMutation.isPending;

  return (
    <div className="mx-auto w-full max-w-5xl px-6 py-10">
      <header className="mb-8">
        <p className="smallcaps text-oxblood-500">Admin</p>
        <h1 className="font-display text-3xl text-espresso-700">Users</h1>
        <p className="mt-1 text-sm text-warmgray-500">
          One row per provisioned user. Click the cap to edit. Soft-delete blocks
          their <span className="font-serif italic">/chat</span> calls without
          dropping their history.
        </p>
      </header>

      <section className="rounded border border-ruleline bg-parchment-100">
        {usersQuery.isLoading ? (
          <p className="px-4 py-6 text-sm italic text-warmgray-400">Loading users…</p>
        ) : usersQuery.isError ? (
          <p className="px-4 py-6 text-sm italic text-oxblood-500">
            Couldn't load users: {(usersQuery.error as Error)?.message ?? "unknown"}
          </p>
        ) : users.length === 0 ? (
          <p className="px-4 py-6 text-sm italic text-warmgray-400">
            No provisioned users yet. They'll appear here after their first chat.
          </p>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-ruleline">
                <th className="px-3 py-2 text-[0.65rem] uppercase tracking-wider text-warmgray-500">
                  User
                </th>
                <th className="px-3 py-2 text-[0.65rem] uppercase tracking-wider text-warmgray-500">
                  Cap
                </th>
                <th className="px-3 py-2 text-[0.65rem] uppercase tracking-wider text-warmgray-500">
                  Month-to-date
                </th>
                <th className="px-3 py-2 text-[0.65rem] uppercase tracking-wider text-warmgray-500">
                  Last active
                </th>
                <th className="px-3 py-2 text-[0.65rem] uppercase tracking-wider text-warmgray-500">
                  Status
                </th>
                <th className="px-3 py-2 text-[0.65rem] uppercase tracking-wider text-warmgray-500 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow
                  key={u.user_id}
                  user={u}
                  isPending={anyPending}
                  onPatch={(userId, patch) => patchMutation.mutate({ userId, patch })}
                  onDelete={(userId) => deleteMutation.mutate(userId)}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>

      <p className="mt-4 text-[0.65rem] text-warmgray-400">
        Hint: changes here update <span className="font-mono">user_spend_caps</span> on
        Neon. Re-enable a soft-deleted user via the "re-enable" link in their row.
      </p>

      <InvitesPanel />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Invites panel (Stage B.3b). Lives inside the AdminUsers page so the admin
// has one place to invite + manage friends.
// ---------------------------------------------------------------------------


function InviteRow({
  entry,
  onRevoke,
  isPending,
}: {
  entry: AllowlistEntry;
  onRevoke: (email: string) => void;
  isPending: boolean;
}) {
  const isActive = !entry.revoked_at;
  return (
    <tr
      className={cn(
        "border-b border-ruleline transition-colors hover:bg-parchment-50",
        !isActive && "opacity-50",
      )}
    >
      <td className="px-3 py-3 align-top">
        <div className="font-mono text-xs text-espresso-700">{entry.email}</div>
        {entry.invited_by ? (
          <div className="mt-0.5 text-[0.65rem] text-warmgray-400">
            invited by {entry.invited_by.replace(/^[^|]+\|/, "")}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-3 align-top text-xs text-warmgray-500">
        {entry.invited_at ? new Date(entry.invited_at).toLocaleDateString() : "—"}
      </td>
      <td className="px-3 py-3 align-top">
        {isActive ? (
          <span className="inline-block text-[0.65rem] uppercase tracking-wider text-warmgray-400">
            active
          </span>
        ) : (
          <span className="inline-block rounded-sm bg-oxblood-50 px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-wider text-oxblood-500">
            revoked
          </span>
        )}
      </td>
      <td className="px-3 py-3 align-top text-right">
        {isActive ? (
          <button
            type="button"
            disabled={isPending}
            onClick={() => {
              if (window.confirm(`Revoke access for ${entry.email}?`)) {
                onRevoke(entry.email);
              }
            }}
            className="text-xs text-oxblood-500 hover:underline disabled:opacity-50"
          >
            revoke
          </button>
        ) : null}
      </td>
    </tr>
  );
}


function InvitesPanel() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");

  const invitesQuery = useQuery({
    queryKey: ["admin", "invites"],
    queryFn: fetchInvites,
  });

  const createMutation = useMutation({
    mutationFn: (e: string) => createInvite(e),
    onSuccess: (data) => {
      const status = data.auth0.exists
        ? "already has an Auth0 account"
        : "new — share the URL below";
      toast.success(`${data.invite.email} invited (${status})`);
      setEmail("");
      queryClient.invalidateQueries({ queryKey: ["admin", "invites"] });
    },
    onError: (err: Error) => {
      toast.error(`Invite failed: ${err.message}`);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (e: string) => revokeInvite(e),
    onSuccess: () => {
      toast.success("Invite revoked");
      queryClient.invalidateQueries({ queryKey: ["admin", "invites"] });
    },
    onError: (err: Error) => {
      toast.error(`Revoke failed: ${err.message}`);
    },
  });

  const invites = invitesQuery.data?.invites ?? [];
  const anyPending = createMutation.isPending || revokeMutation.isPending;

  return (
    <section className="mt-10">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="font-display text-2xl text-espresso-700">Invites</h2>
        <p className="text-xs text-warmgray-500">
          Dynamic allowlist — JWT validator reads this in addition to the static
          <span className="font-mono"> AUTH0_ALLOWED_EMAILS</span> secret.
        </p>
      </div>

      <form
        className="mb-4 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = email.trim();
          if (!trimmed) return;
          createMutation.mutate(trimmed);
        }}
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="friend@example.com"
          required
          className="flex-1 border border-ruleline bg-parchment-50 px-3 py-2 font-mono text-sm text-espresso-700 placeholder:text-warmgray-300"
        />
        <button
          type="submit"
          disabled={anyPending || !email.trim()}
          className="border border-oxblood-500 bg-oxblood-500 px-5 py-2 font-serif text-sm text-parchment-50 transition-colors hover:bg-oxblood-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Invite
        </button>
      </form>

      <p className="mb-3 text-[0.65rem] text-warmgray-400">
        Invitees can sign in immediately — the SPA now probes the backend
        on auth instead of relying on a baked-in list. No frontend
        redeploy needed.
      </p>

      <div className="rounded border border-ruleline bg-parchment-100">
        {invitesQuery.isLoading ? (
          <p className="px-4 py-6 text-sm italic text-warmgray-400">Loading invites…</p>
        ) : invitesQuery.isError ? (
          <p className="px-4 py-6 text-sm italic text-oxblood-500">
            Couldn't load invites: {(invitesQuery.error as Error)?.message ?? "unknown"}
          </p>
        ) : invites.length === 0 ? (
          <p className="px-4 py-6 text-sm italic text-warmgray-400">
            No invites yet. Add a friend's email above.
          </p>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-ruleline">
                <th className="px-3 py-2 text-[0.65rem] uppercase tracking-wider text-warmgray-500">
                  Email
                </th>
                <th className="px-3 py-2 text-[0.65rem] uppercase tracking-wider text-warmgray-500">
                  Invited
                </th>
                <th className="px-3 py-2 text-[0.65rem] uppercase tracking-wider text-warmgray-500">
                  Status
                </th>
                <th className="px-3 py-2 text-[0.65rem] uppercase tracking-wider text-warmgray-500 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {invites.map((entry) => (
                <InviteRow
                  key={entry.email}
                  entry={entry}
                  isPending={anyPending}
                  onRevoke={(e) => revokeMutation.mutate(e)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
